// `bs-view-indicators.component.ng1.ts` + `bs-view-detector.service.ng1.ts`, ported.
//
// Four empty divs carrying bootstrap's deprecated `visible-*` classes, and a reader that
// asks which one the browser is currently showing. `chrome-extension.jsp:342` renders the
// component as a SIBLING of `<ui-view class="main">`, directly under `<body>`, on every one
// of careero's six page templates.
//
// ⚠️ IT IS A MEASUREMENT, NOT A LAYOUT. The divs are empty and carry no box of their own, so
// they add nothing to the page; what they do is let script read bootstrap's OWN breakpoints
// rather than a second set of numbers in JavaScript. Upstream's detector is
// `jQuery('.device-xs').is(':visible')` — the one place where the SCSS variables
// (`$screen-xs-max` and friends, from the patched `_variables.scss`) are the single source
// of truth for both the stylesheet and the code. Replacing it with `matchMedia('(max-width:
// 767px)')` would duplicate those numbers, and this repo's bootstrap is patched.
import { useEffect, useRef, useState, type ReactElement } from 'react'

/** Upstream's four, in upstream's order — `_.findKey` returns the first visible. */
export const BS_VIEW_TYPES = ['xs', 'sm', 'md', 'lg'] as const
export type BSViewType = (typeof BS_VIEW_TYPES)[number]

/**
 * ⚠️ TWO CLASSES PER PROBE, DOING DIFFERENT JOBS. `visible-*` is the bootstrap rule that
 * makes exactly one of them displayed; `device-*` is the hook the detector queries. Neither
 * is styled by anything else, and both are reproduced verbatim — a renamed probe is a
 * detector that always answers `undefined`, and nothing else in the app would fail.
 *
 * Exported so the pair can be pinned by a test without rendering: this repo's UI tests are
 * pure logic and the pixel gate covers markup.
 */
export const PROBE_CLASSES = BS_VIEW_TYPES.map((type) => `device-${type} visible-${type}`)

/** The probes. Render ONCE, high in the tree. */
export function BSViewIndicators(): ReactElement {
  return (
    <>
      {PROBE_CLASSES.map((className) => (
        <div key={className} className={className} />
      ))}
    </>
  )
}

/** Just enough of an element to answer jQuery's `:visible`, so this stays testable. */
export interface ProbeElement {
  offsetWidth?: number
  offsetHeight?: number
  getClientRects?: () => { length: number }
}

/** Just enough of a document to find the probes. */
export interface ProbeHost {
  querySelector: (selector: string) => ProbeElement | null
}

/**
 * `is(':visible')` — jQuery's definition, which is what upstream's detector means: an
 * element with no layout boxes at all.
 *
 * ⚠️ NOT `offsetParent === null`, the usual shorthand. It is also null for a
 * `position: fixed` element, and these probes sit wherever the host template puts them —
 * careero renders them under `<body>` on six different templates, and this port renders
 * them beside the router. The dimensions are what jQuery itself reads.
 */
function isVisible(element: ProbeElement | null): boolean {
  if (element === null || element === undefined) return false
  return (
    (element.offsetWidth ?? 0) > 0 ||
    (element.offsetHeight ?? 0) > 0 ||
    (element.getClientRects?.().length ?? 0) > 0
  )
}

/** The current breakpoint, or `undefined` before the probes are laid out. */
export function readViewType(host: ProbeHost | undefined = globalThis.document): BSViewType | undefined {
  if (host === undefined) return undefined
  return BS_VIEW_TYPES.find((type) => isVisible(host.querySelector(`.device-${type}`)))
}

/**
 * Subscribe to the breakpoint.
 *
 * ⚠️ DEBOUNCED AT 100ms, WHICH IS UPSTREAM'S NUMBER AND NOT AN ARBITRARY ONE. The detector
 * emits on `resize`, and a drag of the window edge fires it continuously; every subscriber
 * upstream re-renders on the emission. The debounce is what makes that affordable, so it is
 * carried over rather than left to React's batching — which would not help, because the
 * reads themselves force layout.
 *
 * ⚠️ AND IT READS ON MOUNT. Upstream's service only emits on resize, so a controller that
 * never sees one keeps whatever it computed at construction — fine there, because the
 * service is a singleton built after the DOM. Here the probes mount with the component, so
 * the first read has to happen in an effect or the first answer is always `undefined`.
 */
export function useBSViewType(): BSViewType | undefined {
  const [viewType, setViewType] = useState<BSViewType | undefined>(undefined)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    const read = (): void => {
      setViewType(readViewType())
    }
    read()

    const onResize = (): void => {
      if (timer.current !== undefined) clearTimeout(timer.current)
      timer.current = setTimeout(read, 100)
    }
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      if (timer.current !== undefined) clearTimeout(timer.current)
    }
  }, [])

  return viewType
}
